// frontend/modules/customer-notification/components/common/NotificationTemplateSelector.tsx
import React, { useEffect, useState } from 'react';
import { Select, Spin } from 'antd';
import { useGetAllTextTemplates } from '@/shared/api/client/text-template-controller';
import { TextTemplateDTO, NotificationPlatforms } from '@/shared/api/model';

const { Option } = Select;

interface NotificationTemplateSelectorProps {
    value?: string;
    onChange?: (value: string) => void;
    platform?: NotificationPlatforms;
    className?: string;
    placeholder?: string;
}

const NotificationTemplateSelector: React.FC<NotificationTemplateSelectorProps> = ({
    value,
    onChange,
    platform,
    className,
    placeholder = 'Select template'
}) => {
    const [templateOptions, setTemplateOptions] = useState<Array<{name: string, displayName: string}>>([]);
    
    // Fetch all templates
    const { data: templatesData, isLoading } = useGetAllTextTemplates();
    
    // Process templates into options
    useEffect(() => {
        if (templatesData) {
            const uniqueTemplates = new Map<string, {name: string, displayName: string, platform: string}>();

            templatesData.forEach(template => {
                if (!template.name) return;
                
                // If platform filter is applied, skip templates for other platforms
                if (platform && template.platform !== platform) return;
                
                // Use template name as unique key
                // This essentially groups templates by name regardless of language
                const key = template.name;
                
                // Only add if not already in map
                if (!uniqueTemplates.has(key)) {
                    uniqueTemplates.set(key, {
                        name: template.name,
                        displayName: formatTemplateName(template.name),
                        platform: template.platform || ''
                    });
                }
            });
            
            setTemplateOptions(Array.from(uniqueTemplates.values())
                .map(({name, displayName}) => ({name, displayName}))
                .sort((a, b) => a.displayName.localeCompare(b.displayName)));
        }
    }, [templatesData, platform]);
    
    return (
        <Select
            showSearch
            value={value}
            onChange={onChange}
            className={className}
            placeholder={placeholder}
            loading={isLoading}
            optionFilterProp="children"
            notFoundContent={isLoading ? <Spin size="small" /> : "No templates found"}
            style={{ width: '100%' }}
        >
            {templateOptions.map(option => (
                <Option key={option.name} value={option.name}>
                    {option.displayName}
                </Option>
            ))}
        </Select>
    );
};

// Helper function to format template names for display
const formatTemplateName = (name: string): string => {
    return name
        .replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

export default NotificationTemplateSelector;
